import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Select, TextInput } from "@narmi/design_system";
import { ContentCard, ContextForm, useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import utils from "byzantine/src/utils";
import { useRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import AccountContext from "../../../contexts/AccountContext";
import { useInstitutionSettings } from "../../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import AmountTextInput from "../../../form/AmountTextInput";
import ComboAccountSelector from "../../../ComboAccountSelector";
import azulUtils from "../../../../utils";
import RecipientSelector from "../../RecipientSelector";
import { RecipientDrawer, DRAWER_TYPES } from "../../../RecipientDrawer";
import LimitsDialog from "../../LimitsDialog";

const WireReasonSelector = ({
  wireReasonTypes = [],
  onChange,
  value,
  error,
}) => {
  const { l10n } = useLocalization();
  return (
    <Select
      label={l10n.getString("label-wire-template--wire-reason-required")}
      onChange={onChange}
      value={value}
      errorText={error}
    >
      {wireReasonTypes.map((reason) => (
        <Select.Item key={reason} value={reason}>
          {reason}
        </Select.Item>
      ))}
    </Select>
  );
};
WireReasonSelector.propTypes = {
  wireReasonTypes: PropTypes.array,
  onChange: PropTypes.func,
  value: PropTypes.string,
  error: PropTypes.string,
};

const FormContent = ({ limits = {} }) => {
  const { accounts } = useContext(AccountContext);
  const { disallow_wire_address_pobox: disallowAddressPoBox } = useUserFeatures();
  const recipients = useRecipients();
  const recipientsRef = useRef(recipients);
  recipientsRef.current = recipients;
  const { wire_reason_types } = useInstitutionSettings();
  const wire_reason_required = !!wire_reason_types?.length;
  const { l10n } = useLocalization();
  const [showRecipientDrawer, setShowRecipientDrawer] = useState(false);
  const formContext = useContext(ContextForm.FormContext);
  const { sendNotification } = useNotificationContext();

  // Validation function for recipient selection
  // Uses a ref to always read the latest recipients list, since ContextForm.Field
  // captures the validate function on first render and does not update it.
  const validateRecipient = (recipientId) => {
    if (!recipientId) {
      return "Required";
    }
    const recipient = recipientsRef.current.find((r) => r.id === recipientId);
    if (!recipient) {
      return "Required";
    }

    if (disallowAddressPoBox && recipient.fedwire_destination?.address) {
      // Check if recipient address contains PO Box
      const addressError = utils.validateStreetAddress(
        recipient.fedwire_destination.address,
        false
      );
      if (addressError) {
        return addressError;
      }

      if (recipient.fedwire_destination?.address_2) {
        const address2Error = utils.validateStreetAddressTwo(
          recipient.fedwire_destination.address_2,
          false
        );
        if (address2Error) {
          return address2Error;
        }
      }
    }

    return null;
  };

  const validateAmount = useCallback(
    (amount, { from_account: fromAccount }) => {
      if (!amount) {
        return "Required";
      }
      if (fromAccount) {
        const account = accounts.find((acc) => acc.id === fromAccount);
        if (account) {
          const balance = account.transferableBalanceAsFloat();
          if (azulUtils.parseValueAsFloat(amount) > balance) {
            return l10n.getString("available-balance-error");
          }
        }
      }
      return null;
    },
    [accounts, l10n],
  );

  const limitsForDisplay = {}; // amount and available amount per time period for each transaction type
  Object.keys(limits).forEach((transaction_type) => {
    if (!["wire", "organization_user_wire"].includes(transaction_type)) return; // only want wire lmits here
    limitsForDisplay[transaction_type] = {};

    Object.keys(limits[transaction_type]).forEach((key) => {
      // limits[transaction] has numbers (which represent days) and "amount_available" as keys
      if (Number(key)) {
        // only want number keys for display
        limitsForDisplay[transaction_type][key] = limits[transaction_type][key];
      }
    });
  });

  const isRecipientDestinationIsValid = useMemo(() => {
    const recipient = recipients.find(
      (r) => r.id === formContext?.data?.recipient,
    );
    if (!recipient) return true; // not yet invalid if no recipient selected
    return recipient.fedwire_destination; // valid if fedwire destination
  }, [formContext?.data?.recipient, disallowAddressPoBox, recipients]);
  const handleOpenDrawer = () => {
    setShowRecipientDrawer(true);
  };
  const handleCloseDrawer = (recipientId) => {
    setShowRecipientDrawer(false);
    // if closing drawer and we didn't get what we needed, unset
    if (!isRecipientDestinationIsValid) {
      formContext.dispatchData({
        type: "updateField",
        field: "recipient",
        update: null,
      });
      sendNotification({
        type: "negative",
        text: l10n.getString("add-fedwire-details-error-banner"),
      });
      return;
    }
    // as this could be a brand new recipient, select the id
    formContext.dispatchData({
      type: "updateField",
      field: "recipient",
      update: recipientId,
    });
  };
  useEffect(() => {
    // if recipient does not have fedwire destination, open drawer to prompt user to add details.
    if (!isRecipientDestinationIsValid) {
      handleOpenDrawer();
    }
  }, [isRecipientDestinationIsValid]);

  return (
    <ContentCard kind="bordered">
      <div className="nds-sans margin--bottom--l wire-transfer-form-header">
        Wire details
      </div>
      <ContextForm.Field required>
        <ComboAccountSelector
          field="from_account"
          label={l10n.getString("label-from", null, "From")}
          accounts={accounts.filter(
            (a) => a.isInternal() && a.isValidTransferSource(),
          )}
          showAddExternalAccountLink={false}
        />
      </ContextForm.Field>
      <ContextForm.Field validate={validateRecipient}>
        <RecipientSelector
          field="recipient"
          label={l10n.getString("label-to")}
          recipients={recipients}
          onAddRecipientClick={handleOpenDrawer}
          displayBottomAction
        />
      </ContextForm.Field>
      <RecipientDrawer
        handleClose={handleCloseDrawer}
        isOpen={showRecipientDrawer}
        fromACHFlow={false}
        fromDomesticWireFlow={true}
        recipientId={formContext?.data?.recipient}
        drawerType={
          formContext?.data?.recipient ? DRAWER_TYPES.edit : DRAWER_TYPES.add
        }
      />
      <ContextForm.Field required validate={validateAmount}>
        <AmountTextInput
          field="amount"
          label={l10n.getString("label-amount", null, "Amount")}
        />
      </ContextForm.Field>
      {wire_reason_required ? (
        <ContextForm.Field required>
          <WireReasonSelector
            field="wire_reason"
            wireReasonTypes={wire_reason_types}
          />
        </ContextForm.Field>
      ) : null}
      <ContextForm.Field
        required
        style={{ marginTop: "20px", marginBottom: 0 }}
      >
        <TextInput
          field="memo"
          label={l10n.getString("label-memo", null, "Memo")}
          maxLength={140}
        />
      </ContextForm.Field>
      <div className="margin--top--s">
        <LimitsDialog
          type={["domestic_wire"]}
          limitsForDisplay={limitsForDisplay}
        />
      </div>
    </ContentCard>
  );
};
FormContent.propTypes = {
  limits: PropTypes.object,
};

export default FormContent;
